import {
  combineLatest,
  distinctUntilChanged,
  map,
  of,
  switchMap,
  type Observable,
  type ObservableInput,
} from 'rxjs';
import { connect, dispatchAction } from '@ardoq/rxbeach';
import type { ComponentType } from 'react';
import type { I18nLocale } from '@ardoq/i18n';
import {
  resolveLaunchStepIndex,
  TourView,
  type ActiveTour,
  type TourDefinition,
  type TourId,
  type TrackTourEvent,
} from '@ardoq/product-tours';
import { endTour, nextTourStep, prevTourStep } from './actions';
import { productTourStatus$, type TourState } from './productTourStatus$';

/** The tours one app registers. Partial: each app hosts a subset of the repo's tours. */
export type TourRegistry = Partial<Record<TourId, Observable<TourDefinition>>>;

type ProductTourProps = {
  activeTour: ActiveTour | null;
  i18nLocale: I18nLocale;
  trackTourEvent: TrackTourEvent;
};

const goToNextStep = () => dispatchAction(nextTourStep());
const goToPreviousStep = () => dispatchAction(prevTourStep());
const closeTour = () => dispatchAction(endTour());

const ProductTourBase = ({
  activeTour,
  i18nLocale,
  trackTourEvent,
}: ProductTourProps) => {
  if (!activeTour) return null;
  return (
    <TourView
      activeTour={activeTour}
      i18nLocale={i18nLocale}
      trackTourEvent={trackTourEvent}
      onNext={goToNextStep}
      onPrev={goToPreviousStep}
      onClose={closeTour}
    />
  );
};

const toActiveTour = (
  tourState: TourState,
  definition: TourDefinition
): ActiveTour | null =>
  tourState.isActive
    ? {
        definition,
        stepIndex: resolveLaunchStepIndex(definition, tourState.stepIndex),
      }
    : null;

const toActiveTour$ = (
  definition$: Observable<TourDefinition>
): Observable<ActiveTour | null> =>
  combineLatest([productTourStatus$, definition$]).pipe(
    map(([tourState, definition]) => toActiveTour(tourState, definition))
  );

const toActiveTourId = (tourState: TourState): TourId | null =>
  tourState.isActive ? tourState.tourId : null;

const activeTourId$: Observable<TourId | null> = productTourStatus$.pipe(
  map(toActiveTourId),
  distinctUntilChanged()
);

export const toProductTourViewModel$ = (
  tourRegistry: TourRegistry,
  i18nLocale$: ObservableInput<I18nLocale>,
  trackTourEvent$: ObservableInput<TrackTourEvent>
): Observable<ProductTourProps> =>
  combineLatest([
    activeTourId$.pipe(
      switchMap(activeTourId => {
        const definition$ = activeTourId
          ? tourRegistry[activeTourId]
          : undefined;
        return definition$ ? toActiveTour$(definition$) : of(null);
      })
    ),
    i18nLocale$,
    trackTourEvent$,
  ]).pipe(
    map(([activeTour, i18nLocale, trackTourEvent]) => ({
      activeTour,
      i18nLocale,
      trackTourEvent,
    }))
  );

/**
 * The tour host an app mounts once, high enough to outlive any step's own
 * navigation. The registry, the app's locale stream and its tracker stream are
 * injected so each app decides which tours it hosts and supplies its own i18n
 * and analytics wiring. The tracker is a stream because an app may only know
 * whether it can report events once it has resolved something itself — the
 * presentation viewer withholds tracking until it knows it has a session.
 */
export const createProductTourView = (
  tourRegistry: TourRegistry,
  i18nLocale$: ObservableInput<I18nLocale>,
  trackTourEvent$: ObservableInput<TrackTourEvent>
): ComponentType =>
  connect(
    ProductTourBase,
    toProductTourViewModel$(tourRegistry, i18nLocale$, trackTourEvent$)
  );
